/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { BankResponse } from '../models/bank-response';
import { BusinessType } from '../models/business-type';
import { CreateBankRequest } from '../models/create-bank-request';
import { GenericResponse } from '../models/generic-response';
import { GetBankResponse } from '../models/get-bank-response';
import { UpdateBankRequest } from '../models/update-bank-request';

/**
 * Bank APIs
 */
@Injectable({ providedIn: 'root' })
export class BankService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllbanks()` */
  static readonly GetAllbanksPath = '/api/v1/banks';

  /**
   * Get list of banks.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllbanks()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllbanks$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields.
       * - name
       * - arabicName
       * - accountName
       * - accountNameArabic
       * - swiftCode
       * - ibanNumber
       */
      search?: string;

      /**
       * This adds a filter to the bank list
       *  - business type
       */
      bankByBusinessType?: BusinessType;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetBankResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      BankService.GetAllbanksPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('bankByBusinessType', params.bankByBusinessType, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetBankResponse>;
        })
      );
  }

  /**
   * Get list of banks.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllbanks$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllbanks(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields.
       * - name
       * - arabicName
       * - accountName
       * - accountNameArabic
       * - swiftCode
       * - ibanNumber
       */
      search?: string;

      /**
       * This adds a filter to the bank list
       *  - business type
       */
      bankByBusinessType?: BusinessType;
    },
    context?: HttpContext
  ): Observable<GetBankResponse> {
    return this.getAllbanks$Response(params, context).pipe(
      map((r: StrictHttpResponse<GetBankResponse>): GetBankResponse => r.body)
    );
  }

  /** Path part for operation `createBank()` */
  static readonly CreateBankPath = '/api/v1/banks';

  /**
   * Create new Bank.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createBank()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createBank$Response(
    params: {
      body: CreateBankRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<BankResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      BankService.CreateBankPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<BankResponse>;
        })
      );
  }

  /**
   * Create new Bank.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createBank$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createBank(
    params: {
      body: CreateBankRequest;
    },
    context?: HttpContext
  ): Observable<BankResponse> {
    return this.createBank$Response(params, context).pipe(
      map((r: StrictHttpResponse<BankResponse>): BankResponse => r.body)
    );
  }

  /** Path part for operation `getBankById()` */
  static readonly GetBankByIdPath = '/api/v1/banks/{id}';

  /**
   * Get bank details using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getBankById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getBankById$Response(
    params: {
      /**
       * The bank's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<BankResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      BankService.GetBankByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<BankResponse>;
        })
      );
  }

  /**
   * Get bank details using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getBankById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getBankById(
    params: {
      /**
       * The bank's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<BankResponse> {
    return this.getBankById$Response(params, context).pipe(
      map((r: StrictHttpResponse<BankResponse>): BankResponse => r.body)
    );
  }

  /** Path part for operation `disableBank()` */
  static readonly DisableBankPath = '/api/v1/banks/{id}';

  /**
   * disable banks.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `disableBank()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableBank$Response(
    params: {
      /**
       * The banks's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      BankService.DisableBankPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * disable banks.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `disableBank$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableBank(
    params: {
      /**
       * The banks's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.disableBank$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateBankById()` */
  static readonly UpdateBankByIdPath = '/api/v1/banks/{id}';

  /**
   * Update bank by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateBankById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateBankById$Response(
    params: {
      /**
       * The bank's id that needs to be updated
       */
      id: number;
      body: UpdateBankRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<BankResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      BankService.UpdateBankByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<BankResponse>;
        })
      );
  }

  /**
   * Update bank by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateBankById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateBankById(
    params: {
      /**
       * The bank's id that needs to be updated
       */
      id: number;
      body: UpdateBankRequest;
    },
    context?: HttpContext
  ): Observable<BankResponse> {
    return this.updateBankById$Response(params, context).pipe(
      map((r: StrictHttpResponse<BankResponse>): BankResponse => r.body)
    );
  }
}
